import { Card } from "@/components/ui/card";
import { useEliminationBracket } from "@/hooks/useEliminationBracket";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin } from "lucide-react";

interface EliminationViewProps {
  eventId: string;
  category?: string | null;
  subcategory?: string | null;
}

export function EliminationView({ eventId, category, subcategory }: EliminationViewProps) {
  const { matches, loading } = useEliminationBracket(eventId, category, subcategory);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <Card className="p-8 text-center">
        <div className="space-y-2">
          <p className="text-lg font-medium">Fase de Eliminación</p>
          <p className="text-muted-foreground">
            La fase de eliminación comenzará una vez finalice la fase de grupos
          </p>
        </div>
      </Card>
    );
  }

  // Group matches by phase
  const matchesByPhase = {
    octavos: matches.filter(m => m.phase === 'octavos'),
    cuartos: matches.filter(m => m.phase === 'cuartos'),
    semifinal: matches.filter(m => m.phase === 'semifinal'),
    final: matches.filter(m => m.phase === 'final')
  };

  const getPhaseLabel = (phase: string) => {
    switch (phase) {
      case 'octavos': return 'Octavos de Final';
      case 'cuartos': return 'Cuartos de Final';
      case 'semifinal': return 'Semifinal';
      case 'final': return 'Final';
      default: return phase;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-2">Fase de Eliminación</h3>
        <p className="text-sm text-muted-foreground">
          Organigrama de la fase eliminatoria del torneo
        </p>
      </div>

      {/* Phase Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(matchesByPhase).map(([phase, phaseMatches]) => 
          phaseMatches.length > 0 && (
            <Card key={phase} className="p-4 text-center">
              <p className="text-sm text-muted-foreground mb-1">
                {getPhaseLabel(phase)}
              </p>
              <p className="text-2xl font-bold">{phaseMatches.length}</p>
              <p className="text-xs text-muted-foreground mt-1">partidos</p>
            </Card>
          )
        )}
      </div>

      {/* Matches List */}
      <div className="space-y-6">
        {Object.entries(matchesByPhase).map(([phase, phaseMatches]) => 
          phaseMatches.length > 0 && (
            <div key={phase}>
              <h4 className="text-lg font-semibold mb-3">{getPhaseLabel(phase)}</h4>
              <div className="space-y-3">
                {phaseMatches.map((match) => (
                  <Card key={match.id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2 flex-1">
                          <div className={`font-medium ${match.winner === 1 ? 'text-primary' : ''}`}>
                            {match.team1}
                            {match.winner === 1 && ' 🏆'}
                          </div>
                          <div className={`font-medium ${match.winner === 2 ? 'text-primary' : ''}`}>
                            {match.team2}
                            {match.winner === 2 && ' 🏆'}
                          </div>
                        </div>
                        {match.score && (
                          <Badge variant="secondary" className="ml-4">
                            {match.score}
                          </Badge>
                        )}
                      </div>
                      
                      {(match.scheduledDate || match.court) && (
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground pt-2 border-t">
                          {match.scheduledDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{match.scheduledDate}</span>
                            </div>
                          )}
                          {match.court && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              <span>{match.court}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
